// ═══════════════════════════════════════════════════════════════════════════════════════════════
// ParcelModal save-path rules, pure so they are unit-tested (src/__tests__/parcel-save.test.js).
//
// THE RULE (2026-10-06): a Save writes only what the user changed. What the screen merely DISPLAYED — a
// default ("Value Pending" for a year with no status), a base-row fallback (owner, account, deadline… shown
// when the year row is empty), a derived figure (taxable = market × ratio, a Proposed level built from a
// scanned notice) — is never written by a Save or a year switch. Derived figures are stored only through the
// explicit "Use derived values" button, or once the user edits that level.
// ═══════════════════════════════════════════════════════════════════════════════════════════════

// A number as typed or stored, KEEPING ITS SIGN. "-830,093" → -830093; "(1,200)" (accounting) → -1200;
// "$1,234.50" → 1234.5; blank / non-numeric → null. The old parsers stripped every non-digit including the
// minus, so PTMS's −830,093 improvement value was written back as +830,093 on the next save.
export function parseSigned(v) {
  if (v == null) return null;
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  const s = String(v).trim();
  if (!s) return null;
  const negative = /^-/.test(s.replace(/^\$/, '')) || /^\(.*\)$/.test(s);
  const digits = s.replace(/[^0-9.]/g, '');
  if (!digits) return null;
  const n = parseFloat(digits);
  if (!Number.isFinite(n)) return null;
  return negative ? -n : n;
}

// Two year-field payloads (both built by the same yearFieldsFrom) → only the fields that differ.
// `saved` is the snapshot taken at load, so it already carries the displayed defaults and base-row
// fallbacks: a field the user did not touch compares equal and is dropped.
const same = (a, b) => {
  const blank = (x) => x == null || (typeof x === 'string' && x.trim() === '');
  if (blank(a) && blank(b)) return true;
  if (blank(a) || blank(b)) return false;
  if (typeof a === 'number' || typeof b === 'number') return Number(a) === Number(b);
  return String(a).trim() === String(b).trim();
};
export function changedYearFields(current, saved) {
  const out = {};
  for (const k of Object.keys(current)) if (!same(current[k], (saved || {})[k])) out[k] = current[k];
  return out;
}

// Market Total after an edit. With ANY component present the total is their sum (a zero sum stored as null,
// the convention recomputeTotals has always used); with every component blank, a stated total is kept. A
// capped FL row stores the just value as market_total with blank components (the land/imp split sums to the
// capped value), and the old `sum || null` wiped it on any edit in that row.
// ⛔ BUT A TOTAL THAT WAS THE SUM OF THE COMPONENTS JUST CLEARED IS NOT A STATED TOTAL. `prev` is the row before
// this edit: if it had components, its total was their sum, and clearing the last of them blanks it. Without this,
// clearing Land (25,815,843) then Imp (−830,093) on 30-3129-029-0010 / 2024 left −830,093 — the sum after the
// first clear — standing as though someone had typed it (Philip, 2026-10-09). A stated total is typed into Market
// Total itself, which the screen allows only while every component is blank.
const MARKET_PARTS = ['market_land', 'market_imp', 'market_pp'];
const TAXABLE_PARTS = ['taxable_land', 'taxable_imp', 'taxable_pp'];
const anyPart = (row, parts) => parts.some(f => parseSigned(row?.[f]) != null);

export function recomputedMarketTotal(row, prev) {
  const parts = MARKET_PARTS.map(f => parseSigned(row?.[f]));
  if (parts.some(p => p != null)) return parts.reduce((s, p) => s + (p ?? 0), 0) || null;
  if (anyPart(prev, MARKET_PARTS)) return null;
  return parseSigned(row?.market_total);
}

// Taxable Subtotal after an edit: the components' sum when it is non-zero, else a stated subtotal (total-only
// jurisdictions like the FL school column, whose TRIM notice gives only a taxable total). Same stale-sum guard as
// Market Total: clearing the last taxable component blanks a subtotal that was only their sum.
export function recomputedTaxableSubtotal(row, prev) {
  const sum = TAXABLE_PARTS.reduce((s, f) => s + (parseSigned(row?.[f]) ?? 0), 0);
  if (sum) return sum;
  if (!anyPart(row, TAXABLE_PARTS) && anyPart(prev, TAXABLE_PARTS)) return null;
  return parseSigned(row?.taxable_subtotal);
}

// Market Total is TYPEABLE only while every market component is blank — then it is a stated figure (a board
// decision or bill that gives only a total). Once any component is filled it is their sum again.
export const marketTotalIsStated = (row) => !anyPart(row, MARKET_PARTS);

// The appeal levels a Save may write: populated AND edited. Derived-only levels are not "edited".
export function levelsToPersist(appeals, dirtyLevels) {
  return Object.keys(appeals || {}).filter(l => dirtyLevels?.[l] && Object.keys(appeals[l] || {}).length > 0);
}

// Flags the screen sets on rows it derived rather than loaded. Never stored (saveAppealLevel picks columns).
export const DERIVED_FLAGS = ['_derived_taxable', '_seeded_from_scanner'];
export const levelHasDerived = (rows) => Object.values(rows || {}).some(r => DERIVED_FLAGS.some(f => r?.[f]));
